import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Users as UsersIcon, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { InlineEditorShell } from "@/components/InlineEditorShell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";
import { StatusSquare } from "./_tenantCommon.jsx";
import { useAuth } from "@/lib/auth.jsx";
import { useConfirm } from "@/lib/confirm.jsx";
import { toast } from "sonner";

export default function UsersPage() {
  const { t } = useTranslation();
  const { role, user } = useAuth();
  const confirm = useConfirm();
  const canEdit = role === "owner";
  const myEmail = (user?.email || user?.user?.email || "").toLowerCase();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", role: "operator" });

  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  // A row represents "me" when the email matches the logged-in owner. Used
  // to disable self-destructive controls (disable own account, demote self,
  // delete self) — the backend enforces these too, but surfacing them in the
  // UI avoids a confusing 400 toast.
  const isSelf = (u) => myEmail && (u.email || "").toLowerCase() === myEmail;

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success(t("pages.users.createdToast"));
      setOpen(false);
      setForm({ email: "", password: "", role: "operator" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };
  const toggle = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { enabled: !u.enabled });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };
  const changeRole = async (u, r) => {
    try {
      await api.patch(`/users/${u.id}`, { role: r });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
      load(); // reset the select back to the server-truth role
    }
  };
  const del = async (u) => {
    const _ok = await confirm({ title: t("confirm.deleteUser", { name: u.email }), danger: true, confirmLabel: t("common.delete") || "Delete" });
    if (!_ok) return;
    try {
      await api.delete(`/users/${u.id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };

  // Phase N+ — 3CX/Vodia-style inline editor takes over the whole page.
  if (open) {
    return (
      <InlineEditorShell
        crumb={t("pages.users.title")}
        title={t("pages.users.dialogTitle")}
        onCancel={() => {
          setOpen(false);
          setForm({ email: "", password: "", role: "operator" });
        }}
        onSave={save}
        saveLabel={t("common.create")}
        saveDisabled={!form.email?.trim() || !form.password?.trim()}
        testidRoot="user-editor"
      >
        <div className="space-y-3">
          <div>
            <Label>{t("fields.email")}</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="new-user-email"
            />
          </div>
          <div>
            <Label>{t("fields.password")}</Label>
            <PasswordInput
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>
          <div>
            <Label>{t("fields.role")}</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="owner">{t("pages.users.roleOwnerDesc")}</option>
              <option value="operator">{t("pages.users.roleOperatorDesc")}</option>
              <option value="readonly">{t("pages.users.roleReadonlyDesc")}</option>
            </select>
          </div>
        </div>
      </InlineEditorShell>
    );
  }

  return (
    <div data-testid="page-users">
      <PageHeader
        title={t("pages.users.title")}
        subtitle={t("pages.users.subtitle")}
        icon={UsersIcon}
        actions={
          canEdit && (
            <Button onClick={() => setOpen(true)} data-testid="new-user-btn">
              <Plus className="h-4 w-4 mr-1" /> {t("pages.users.newBtn")}
            </Button>
          )
        }
      />
      {items.length === 0 ? (
        <EmptyState
          title={t("pages.users.emptyTitle")}
          description={t("pages.users.emptyDesc")}
        />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                <TableHead className="min-w-[200px]">{t("fields.email")}</TableHead>
                <TableHead className="w-[180px]">{t("fields.role")}</TableHead>
                <TableHead className="w-[100px]">{t("firewall.cols.enabled")}</TableHead>
                <TableHead className="w-[170px] whitespace-nowrap">{t("pages.users.colCreated")}</TableHead>
                {canEdit && (
                  <TableHead className="w-[80px] text-right pr-3">{t("common.actions")}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((u) => {
                const self = isSelf(u);
                return (
                <TableRow key={u.id} className="row-hover" data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-mono text-sm align-middle">
                    {/* Phase 6n-e — flex-wrap so long emails don't push the
                        "you" badge off-screen on narrow viewports; the
                        badge stays glued to the email visually but can
                        drop to a second line when needed. */}
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="truncate" title={u.email}>{u.email}</span>
                      {self && (
                        <span
                          data-testid="user-self-badge"
                          title="This is your own account"
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-border text-muted-foreground"
                        >
                          <ShieldCheck className="h-3 w-3" /> you
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block">
                              <select
                                className="h-8 rounded border border-input bg-background px-2 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                value={u.role}
                                disabled={self}
                                onChange={(e) => changeRole(u, e.target.value)}
                              >
                                <option value="owner">{t("pages.users.roleOwner")}</option>
                                <option value="operator">{t("pages.users.roleOperator")}</option>
                                <option value="readonly">{t("pages.users.roleReadonly")}</option>
                              </select>
                            </span>
                          </TooltipTrigger>
                          {self && (
                            <TooltipContent>
                              You can't change your own role — ask another owner.
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <StatusPill kind="info">{u.role}</StatusPill>
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block">
                              <Switch
                                checked={!!u.enabled}
                                disabled={self}
                                onCheckedChange={() => toggle(u)}
                              />
                            </span>
                          </TooltipTrigger>
                          {self && (
                            <TooltipContent>
                              You can't disable your own account.
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      // Phase 6n-e — readonly viewers see a square +
                      // label instead of a pill. Matches Customers,
                      // Live Traffic, Trunks reachability cells.
                      <div className="flex items-center gap-2">
                        <StatusSquare
                          kind={u.enabled ? "ok" : "offline"}
                          title={u.enabled ? t("common.enabled") : t("common.disabled")}
                        />
                        <span className="text-xs">
                          {u.enabled ? t("common.enabled") : t("common.disabled")}
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {/* Phase 6n-e — compact date format keeps the column
                        from blowing out on US locales (was up to 22ch
                        with toLocaleString). YYYY-MM-DD HH:MM is 16ch
                        and reads identically across locales. */}
                    {(() => {
                      const d = new Date(u.created_at);
                      if (Number.isNaN(d.getTime())) return "—";
                      const pad = (n) => String(n).padStart(2, "0");
                      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                    })()}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-[hsl(var(--danger))] disabled:opacity-40"
                                disabled={self}
                                onClick={() => del(u)}
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {self && (
                            <TooltipContent>
                              You can't delete your own account.
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  )}
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
